import { Theme, Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAppSelector } from '../app/hooks';
import IssueBacklogLobbyCard from '../components/IssueReport/IssueBacklogLobbyCard';
import TaskRouterBody from '../components/TaskRouterBody';
import { selectWSConnectionMessage } from '../redux/features/taskRouter/taskRouter.selector';
import logger from '../utils/logger';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  taskRouterPageClass: {
    /**
     * The lobby is ONE screen that never scrolls, and the backlog card is a real
     * child of it rather than an overlay — so the page is the flex column that
     * divides the space between the two.
     *
     * HITL-1575 first floated the card (`position: fixed`, bottom-left) to keep this
     * page's layout untouched. That was wrong: it covered the centred "Get ready"
     * message, which is the one thing this screen exists to say. `TaskRouterBody`
     * carries a definite height, so as a flex item that height is its BASIS and it
     * shrinks by exactly the card's height — the message re-centres in what is left
     * and nothing overlaps, with no change to the body itself.
     *
     * Properties added to the existing rule rather than a new class: this page has a
     * snapshot, and JSS numbers class names off a global rule counter, so a new rule
     * here renumbers unrelated suites.
     */
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  alertClass: {
    position: 'absolute',
    left: spacing(2),
    right: spacing(2),
    zIndex: 200,
    alignItems: 'center',
  },
}));

const TaskRouterPage = () => {
  const { taskRouterPageClass, alertClass } = useStyles();
  const {
    handleAgentAvailability,
    sendTaskMessage,
    initializeTaskRouter,
    isTaskAssignmentMode,
  } = useOutletContext<any>();
  const wsConnectionMessage = useAppSelector(selectWSConnectionMessage);

  useEffect(() => {
    logger.info({
      message: 'Task-router page component is mounted',
      isTR: true,
    });
    initializeTaskRouter();
  }, []);

  return (
    <div data-testid="task-router-page" className={taskRouterPageClass}>
      {wsConnectionMessage ? (
        <Alert variant="filled" severity="error" className={alertClass}>
          {wsConnectionMessage}
        </Alert>
      ) : (
        <TaskRouterBody
          handleAgentAvailability={handleAgentAvailability}
          sendTaskMessage={sendTaskMessage}
          isTaskAssignmentMode={isTaskAssignmentMode}
        />
      )}

      {/* A SIBLING of the body, never inside it — see the card's own note on
          `TaskRouterAcceptance`'s window-level keyup. Outside the ternary too, so the
          leftover work is still reachable while the WS-error alert is up: the report
          send is REST and independent of that socket, and an outage is dead time like
          any other.

          LAST in the column, and it renders nothing at all when there is nothing to
          classify — so a clean lobby is laid out exactly as it was before this
          existed. */}
      <IssueBacklogLobbyCard isTaskAssignmentMode={isTaskAssignmentMode} />
    </div>
  );
};

export default TaskRouterPage;
